/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { CacheType, createObservableDataAction, IAction, IActionContext, IActionInput } from '@msdyn365-commerce/core';
import { getCheckoutState } from '@msdyn365-commerce/global-state';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { getSimpleProducts, ProductInput } from '@msdyn365-commerce-modules/retail-actions';

/**
 * Input class for ActiveCheckoutCartWithProductsDetails data action.
 */
export class ActiveCheckoutCartProductsDetailsInput implements IActionInput {
    public getCacheKey = () => 'ActiveCheckoutCartProducts';

    public getCacheObjectType = () => 'ActiveCheckoutCartProducts';

    public dataCacheType = (): CacheType => 'none';
}

export const createCartInput = () => {
    return new ActiveCheckoutCartProductsDetailsInput();
};

/**
 * Calls the Retail API and returns a cart object based on the passed GetCartInput.
 * @param input
 * @param ctx
 */
export async function getActiveCheckoutCartProductsDetailsAction(
    input: ActiveCheckoutCartProductsDetailsInput,
    ctx: IActionContext
): Promise<SimpleProduct[]> {
    // Check if the input is valid
    if (!input) {
        ctx.telemetry.exception(new Error('[getActiveCheckoutCartWithProductsDetails]No valid Input was provided, failing'));
        throw new Error('[getActiveCheckoutCartWithProductsDetails]No valid Input was provided, failing');
    }

    const checkoutState = await getCheckoutState(ctx);
    const cart = checkoutState.checkoutCart.cart;

    // If there are cart lines, make call to get products
    if (!checkoutState.checkoutCart.hasInvoiceLine && cart && cart.CartLines && cart.CartLines.length > 0) {
        return getSimpleProducts(
            <ProductInput[]>cart.CartLines.map(cartLine => {
                if (cartLine.ProductId) {
                    return new ProductInput(
                        cartLine.ProductId,
                        ctx.requestContext.apiSettings,
                        undefined,
                        undefined,
                        ctx.requestContext,
                        cartLine.CatalogId
                    );
                }
                return undefined;
            }).filter(Boolean),
            ctx
        )
            .then(products => {
                if (products) {
                    if (typeof window !== 'undefined') {
                        window.dispatchEvent(
                            new CustomEvent('datalayerEntityLoaded', {
                                detail: {
                                    typeName: 'ACTIVECHECKOUTCARTPRODUCTS',
                                    key: 'ACTIVECHECKOUTCARTPRODUCTS',
                                    result: products
                                }
                            })
                        );
                    }
                    return products;
                }
                return [];
            })
            .catch(error => {
                ctx.telemetry.exception(error);
                throw new Error('[getActiveCheckoutCartWithProductsDetails]Unable to hydrate cart with product information');
            });
    }

    return <SimpleProduct[]>[];
}

export const getActiveCheckoutCartProductsDetailsActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/checkout/get-products-details-in-active-checkout-cart',
    action: <IAction<SimpleProduct[]>>getActiveCheckoutCartProductsDetailsAction,
    input: createCartInput
});

export default getActiveCheckoutCartProductsDetailsActionDataAction;
